import {
  WORKFLOW_STOP_INTENT_LABELS,
  WORKFLOW_STOP_TYPE_LABELS,
  type WorkflowStopIntent,
  type WorkflowStopType,
} from './stopWorkflows';

/**
 * One stop vocabulary (CR-6, §22) — the pure FE mirror of the server's
 * `RouteStopKindHelper` (`route-stop-kind.helper.ts`). Keep the two in step.
 *
 * A route-plan / UJP stop is described by the WORKFLOW vocabulary:
 * `type` (PICKUP | DROP_OFF) + `intent` (GENERIC | UNLOAD | RETURN_CONTAINER)
 * + `pool` (the empty positioning / return leg the client is not charged for
 * unless `chargedPositioning`; since CR-8b `pool` is DERIVED from position +
 * billing by `deriveStopBilling`, never edited by hand). The
 * legacy planner `role` is DERIVED from those three and kept on every
 * persisted stop so pre-CR-6 snapshots still read:
 *
 *   POSITIONING → PICKUP · GENERIC · pool         PICKUP   → PICKUP · GENERIC
 *   RETURN      → DROP_OFF · (intent ?? GENERIC) · pool    DROP_OFF → DROP_OFF · (intent ?? UNLOAD)
 *
 * Every FE reader goes through `kindOf`, so a stop that only carries `role`
 * behaves exactly as before — except a pool DROP_OFF no longer implies
 * RETURN_CONTAINER (business rule: a return-to-pool leg is a plain drop unless
 * ops says otherwise).
 */

/** Legacy planner role — derived since CR-6, still on every response. */
export type RouteStopRole = 'POSITIONING' | 'PICKUP' | 'DROP_OFF' | 'RETURN';

export type RouteStopType = WorkflowStopType;
export type RouteStopIntent = WorkflowStopIntent;

export interface RouteStopKind {
  type: RouteStopType;
  /** PICKUP is always GENERIC; a pool DROP_OFF defaults to GENERIC (the truck
   *  just goes home), a billable DROP_OFF to UNLOAD. */
  intent: RouteStopIntent;
  pool: boolean;
}

/** What a stored or submitted stop may carry: the new keys, the legacy role, or both. */
export interface RouteStopKindInput {
  role?: string | null;
  type?: string | null;
  intent?: string | null;
  pool?: boolean | null;
  /**
   * CR-8 (D1): per-stop billing override for a POOL stop. `null`/absent =
   * follow the client rule (`chargedPositioning`); `true`/`false` beats the
   * rule in either direction (D2). Ignored on a non-pool stop.
   */
  charged?: boolean | null;
}

export const ROUTE_STOP_TYPES: readonly RouteStopType[] = ['PICKUP', 'DROP_OFF'];
export const ROUTE_STOP_INTENTS: readonly RouteStopIntent[] = [
  'GENERIC',
  'UNLOAD',
  'RETURN_CONTAINER',
];

const INTENTS: ReadonlySet<string> = new Set(ROUTE_STOP_INTENTS);
const asIntent = (v: string | null | undefined): RouteStopIntent | null =>
  v && INTENTS.has(v) ? (v as RouteStopIntent) : null;

/** `{ type, intent, pool }` of a stop — from the new keys, else derived from `role`. */
export const kindOf = (stop: RouteStopKindInput): RouteStopKind => {
  if (stop.type === 'PICKUP' || stop.type === 'DROP_OFF') {
    const pool = stop.pool === true;
    if (stop.type === 'PICKUP') return { type: 'PICKUP', intent: 'GENERIC', pool };
    return {
      type: 'DROP_OFF',
      intent: asIntent(stop.intent) ?? (pool ? 'GENERIC' : 'UNLOAD'),
      pool,
    };
  }
  switch (stop.role) {
    case 'POSITIONING':
      return { type: 'PICKUP', intent: 'GENERIC', pool: true };
    case 'RETURN':
      return { type: 'DROP_OFF', intent: asIntent(stop.intent) ?? 'GENERIC', pool: true };
    case 'DROP_OFF':
      return { type: 'DROP_OFF', intent: asIntent(stop.intent) ?? 'UNLOAD', pool: false };
    default:
      return { type: 'PICKUP', intent: 'GENERIC', pool: false };
  }
};

/** The legacy role a kind maps to (sent alongside the new keys for old readers). */
export const roleOf = (kind: RouteStopKind): RouteStopRole => {
  if (kind.type === 'PICKUP') return kind.pool ? 'POSITIONING' : 'PICKUP';
  return kind.pool ? 'RETURN' : 'DROP_OFF';
};

/** The empty positioning / return leg (CR6-D3): not charged unless the client is. */
export const isPool = (stop: RouteStopKindInput): boolean => kindOf(stop).pool;

/**
 * CR-8 (D1/D2), server mirror: is the leg touching this stop billable, given
 * the stop AS THE SERVER STORED IT (`pool` + `charged`)? Non-pool stops always
 * are; a pool stop follows its own `charged` override when set, else the
 * client's `chargedPositioning` rule. Mirrors `RouteStopKindHelper.isCharged`.
 * Readers of a server echo (the UJP detail panel) use this; anything that
 * EDITS a route derives `pool` from position instead (`deriveStopBilling`).
 */
export const isStopCharged = (
  stop: RouteStopKindInput,
  clientDefault: boolean
): boolean => {
  if (!isPool(stop)) return true;
  return typeof stop.charged === 'boolean' ? stop.charged : clientDefault;
};

/** CR-8 (D3), server mirror: a pool stop whose effective billing differs from the client rule. */
export const isChargedOverride = (
  stop: RouteStopKindInput,
  clientDefault: boolean
): boolean =>
  isPool(stop) &&
  typeof stop.charged === 'boolean' &&
  stop.charged !== clientDefault;

/**
 * CR-8b: what position + the billing override say about one stop.
 *
 * A route always starts at the pool and ends back at it, so the pool is by
 * definition the FIRST stop (a PICKUP) and the LAST stop (a DROP_OFF); there
 * is no pool switch. Billing is decided only on those two edge stops:
 *
 *   isEdge           = i === 0 || i === last
 *   effectiveCharged = edge ? (charged ?? clientDefault) : true
 *   pool             = edge && !effectiveCharged
 *
 * An edge stop that IS billed is a plain pickup / drop; an edge stop that is
 * NOT billed is the pool leg. Middle stops are always billed and their stored
 * `charged` is ignored. Sending the derived `pool` + the override `charged`
 * makes the server's `isCharged(stop, clientDefault)` agree with this.
 */
export interface StopBilling {
  isEdge: boolean;
  effectiveCharged: boolean;
  pool: boolean;
  /** The effective value differs from the client rule (edge stops only). */
  override: boolean;
}

export const deriveStopBilling = (
  stops: ReadonlyArray<{ charged?: boolean | null }>,
  clientDefault: boolean
): StopBilling[] => {
  const last = stops.length - 1;
  return stops.map((s, i) => {
    const isEdge = i === 0 || i === last;
    const effectiveCharged = isEdge
      ? typeof s.charged === 'boolean'
        ? s.charged
        : clientDefault
      : true;
    return {
      isEdge,
      effectiveCharged,
      pool: isEdge && !effectiveCharged,
      override: isEdge && effectiveCharged !== clientDefault,
    };
  });
};

/**
 * CR-8 (D4) under the CR-8b derivation: Σ legs whose two endpoints are both
 * billed. Stops are keyed by `key(stop)` (uid in a draft, seq on the wire) so
 * one rule serves the planner draft and the wizard draft. Legs pointing at an
 * unknown stop are skipped.
 */
export const sumChargedLegs = <S extends { charged?: boolean | null }, K>(
  stops: readonly S[],
  legs: ReadonlyArray<{ from: K; to: K; km: number }>,
  key: (stop: S) => K,
  clientDefault: boolean
): number => {
  const billing = deriveStopBilling(stops, clientDefault);
  const billed = new Map<K, boolean>(
    stops.map((s, i) => [key(s), billing[i].effectiveCharged])
  );
  return legs.reduce((sum, l) => {
    const from = billed.get(l.from);
    const to = billed.get(l.to);
    return from && to ? sum + l.km : sum;
  }, 0);
};

/** A stop with all four keys set consistently — what writers send. */
export const normalizeStopKind = (
  stop: RouteStopKindInput
): RouteStopKind & { role: RouteStopRole } => {
  const kind = kindOf(stop);
  return { ...kind, role: roleOf(kind) };
};

/** The default intent a fresh stop of this type/pool gets (mirrors `kindOf`). */
export const defaultIntentFor = (type: RouteStopType, pool: boolean): RouteStopIntent =>
  type === 'PICKUP' ? 'GENERIC' : pool ? 'GENERIC' : 'UNLOAD';

export const POOL_LEG_LABEL = 'Leg pool';

/**
 * Human label for a stop kind, one label set everywhere (CR6-D4):
 * "Pickup", "Pickup · Leg pool", "Drop off · Bongkar", "Drop off · Leg pool",
 * "Drop off · Retur kontainer · Leg pool". A pool drop's implicit GENERIC is
 * not spelled out — "Leg pool" already says what the stop is.
 */
export const stopKindLabel = (stop: RouteStopKindInput | RouteStopKind): string => {
  const kind = kindOf(stop);
  const parts = [WORKFLOW_STOP_TYPE_LABELS[kind.type]];
  if (kind.type === 'DROP_OFF' && !(kind.pool && kind.intent === 'GENERIC')) {
    parts.push(WORKFLOW_STOP_INTENT_LABELS[kind.intent]);
  }
  if (kind.pool) parts.push(POOL_LEG_LABEL);
  return parts.join(' · ');
};
